import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { Box, Typography } from "@mui/material";
import theme from "themeProvider";
import { isPlainObject } from "../../utils";
import TimelineHeader from "./TimelineHeader";
import TimelineColumn from "./TimelineColumn";
import NavButton from "./NavButton";
import Legend from "./Legend";
import { CARD_GAP, SCROLL_STEP, TICK_BAR_HEIGHT } from "./constants";

const EventTimeline = ({ events, isConnected, connectionError }) => {
  const scrollRef = useRef(null);
  const hasScrolledOnceRef = useRef(false);

  // Oldest first (left to right); filter keeps object identity so memoized cards skip re-render.
  const orderedEvents = useMemo(
    () => (Array.isArray(events) ? events.filter(isPlainObject).reverse() : []),
    [events],
  );
  const latestEvent = orderedEvents[orderedEvents.length - 1];
  const latestKey = latestEvent
    ? `${latestEvent.eventId}-${latestEvent.receivedAt}`
    : "";

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !latestKey) return;
    // Instant jump on first paint; smooth-scroll for events after that.
    el.scrollTo?.({
      left: el.scrollWidth,
      behavior: hasScrolledOnceRef.current ? "smooth" : "instant",
    });
    hasScrolledOnceRef.current = true;
  }, [latestKey]);

  const scrollLeft = useCallback(() => {
    scrollRef.current?.scrollBy?.({ left: -SCROLL_STEP, behavior: "smooth" });
  }, []);
  const scrollRight = useCallback(() => {
    scrollRef.current?.scrollBy?.({ left: SCROLL_STEP, behavior: "smooth" });
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        background: theme.palette.common.white,
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: 2,
        mb: 4,
      }}
    >
      <TimelineHeader
        isConnected={!!isConnected}
        connectionError={connectionError}
        eventCount={orderedEvents.length}
      />

      <Box sx={{ position: "relative", minWidth: 0, px: 5, py: 3 }}>
        <NavButton direction="left" onClick={scrollLeft} />
        <NavButton direction="right" onClick={scrollRight} />

        <Box
          sx={{
            position: "absolute",
            left: 24,
            right: 24,
            top: "50%",
            height: TICK_BAR_HEIGHT,
            transform: "translateY(-50%)",
            borderTop: `1px solid ${theme.palette.grey[300]}`,
            backgroundImage: `repeating-linear-gradient(90deg, ${theme.palette.grey[900]} 0 1px, transparent 1px 14px)`,
          }}
        />

        <Box
          ref={scrollRef}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: `${CARD_GAP}px`,
            width: "100%",
            minWidth: 0,
            overflowX: "auto",
            overflowY: "hidden",
            scrollBehavior: "smooth",
            position: "relative",
            "&::-webkit-scrollbar": { height: 0 },
          }}
        >
          {orderedEvents.length ? (
            orderedEvents.map((event, index) => (
              <TimelineColumn
                key={`${event.eventId}-${event.receivedAt}`}
                event={event}
                isTop={index % 2 === 0}
                isLatest={index === orderedEvents.length - 1}
              />
            ))
          ) : (
            <Typography
              sx={{
                color: theme.palette.text.secondary,
                m: "auto",
                py: 8,
                width: "100%",
                textAlign: "center",
              }}
            >
              Waiting for events...
            </Typography>
          )}
        </Box>
      </Box>

      <Legend />
    </Box>
  );
};

export default memo(EventTimeline);
